// ---------- les catégories ----------
//
// Le vocabulaire d'étiquettes que le catalogue pose sur une carte, et que la page des packs pose
// maintenant sur une card de pack. Sorti de home.ts pour cette seule raison : deux écrans le
// lisent, et une deuxième copie des libellés aurait divergé de la première au premier ajout.
//
// La catégorie est un SLUG LIBRE côté serveur, pas un enum (cf. la note de puzzles.category dans
// backend/src/db/schema.ts). Un slug inconnu reçoit quand même son étiquette, libellée par le
// slug lui-même, plutôt que de disparaître de l'écran.
//
// Ce cas est devenu RARE (2026-10-14) : le jeu déclare désormais au serveur les catégories de
// CATEGORY_LABELS, et ne reçoit plus que celles-là (cf. knownCategories dans api.ts). Il ne reste
// que les feuilles qu'un joueur avait déjà commencées avant qu'on les reclasse ailleurs — on les
// lui laisse, sous leur slug brut, plutôt que de les lui reprendre. Le repli reste donc utile.

import { t } from './i18n';

/** Les catégories que le jeu sait nommer. L'ORDRE DE CET OBJET EST L'ORDRE DU FILTRE du
 *  catalogue (cf. catKeys dans home.ts) : les grandes familles de sujets d'abord, les plus
 *  pointues ensuite, et un fourre-tout se lit après les catégories qu'il ramasse, donc `divers`
 *  reste en dernier.
 *
 *  CE QUI EST ICI EST AUSSI CE QUE LE SERVEUR NOUS ENVOIE : les clés sont déclarées à chaque
 *  appel du catalogue et des packs (cf. knownCategories dans api.ts), et une feuille d'une
 *  catégorie absente d'ici ne nous parvient pas. Retirer une ligne retire donc ses feuilles du
 *  catalogue de cette version — sauf celles déjà jouées.
 *
 *  `personnes` s'affiche « Portraits » depuis l'arrivée de `scenes` (2026-10-14) : un visage
 *  d'un côté, quelqu'un qui agit de l'autre. Le slug reste, des feuilles publiées le portent. */
export const CATEGORY_LABELS: Record<string, string> = {
  animaux: t('home.category.animaux'),
  paysage: t('home.category.paysage'),
  monuments: t('home.category.monuments'),
  scenes: t('home.category.scenes'),
  personnes: t('home.category.personnes'),
  celebrite: t('home.category.celebrite'),
  contes: t('home.category.contes'),
  saisonnier: t('home.category.saisonnier'),
  vehicules: t('home.category.vehicules'),
  sport: t('home.category.sport'),
  art: t('home.category.art'),
  divers: t('home.category.divers'),
};

/** Même forme que le validateur du backend. Revérifiée ici parce que la valeur finit à la fois
 *  comme TEXTE et comme NOM DE CLASSE CSS dans un innerHTML : tout ce qui n'a pas cette forme
 *  est écarté plutôt qu'injecté. */
const SLUG = /^[a-z][a-z0-9-]{1,31}$/;

/** Le prédicat, exporté : le catalogue s'en sert pour décider sous quel bouton de filtre un
 *  puzzle compte (cf. catKey dans home.ts), et il doit être LE MÊME que celui qui décide si une
 *  étiquette s'affiche — sinon un puzzle serait filtrable sous une catégorie qui ne s'affiche
 *  nulle part, ou l'inverse. */
export function isCategorySlug(v: string | null | undefined): v is string {
  return !!v && SLUG.test(v);
}

/**
 * L'étiquette d'une catégorie, en balisage. Chaîne VIDE quand il n'y a pas de catégorie — c'est
 * le cas facultatif, et il est fréquent : un puzzle d'avant les catégories n'en a pas, un pack
 * peut parfaitement n'être d'aucune. Rien ne s'affiche alors, plutôt qu'une puce vide.
 */
/**
 * Le SURTITRE d'une card de pack : le nom de la catégorie en petites capitales, au-dessus du
 * titre. Pas la même chose que l'étiquette ci-dessous, et volontairement : sur une card de pack
 * la catégorie est la première chose qu'on lit — « de quoi ça parle » avant « comment ça
 * s'appelle » — alors que sur une carte du catalogue elle est une méta parmi d'autres.
 *
 * Jamais vide : sans catégorie, le pack est une « Sélection ». Un surtitre absent ferait remonter
 * le titre et désalignerait la card de sa voisine, pour rien.
 */
export function categoryEyebrow(category: string | null): string {
  if (!isCategorySlug(category)) return t('packs.selection');
  return CATEGORY_LABELS[category] ?? category;
}

export function categoryTag(category: string | null): string {
  if (!isCategorySlug(category)) return '';
  const label = CATEGORY_LABELS[category] ?? category;
  return `<span class="home-tag cat-${category}">${label}</span>`;
}

/** Les catégories qui ont un symbole à elles dans le sprite d'icônes (cf. la note en fin
 *  d'index.html). Les autres retombent sur le glyphe générique. */
const CATEGORY_MARKS = new Set([
  'celebrite',
  'personnes',
  'paysage',
  'animaux',
  'vehicules',
  'sport',
  'monuments',
  'scenes',
  'contes',
  'saisonnier',
  'divers',
]);

/**
 * L'identifiant du glyphe d'une catégorie dans le sprite — pas le SVG lui-même, parce que ses
 * deux appelants n'en font pas la même chose : filigrane presque effacé derrière une card de
 * pack (src/packs.ts), illustration pleine et teintée sur une card du catalogue (src/home.ts).
 * Chacun pose donc sa classe, et ce module ne tient que la table de correspondance.
 *
 * Sans catégorie — un cas fréquent et légitime — on retombe sur le glyphe des « divers » plutôt
 * que sur rien : une card nue serait la seule de l'écran sans illustration. Un slug qu'aucun
 * symbole ne couvre y retombe aussi, ce qui est voulu quand l'administration en ajoute un avant
 * que l'application le connaisse.
 */
export function categoryMarkId(category: string | null): string {
  // « art » n'a pas de symbole à lui : il emprunte la palette de l'onglet « Mon flux », qui est
  // exactement le bon dessin. Les autres suivent la convention i-cat-<slug>.
  if (category === 'art') return 'i-feed';
  return category && CATEGORY_MARKS.has(category) ? `i-cat-${category}` : 'i-cat-divers';
}
